import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {property} from 'lit/decorators/property.js';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {customElement} from 'lit/decorators/custom-element.js';
import {msg} from '@lit/localize';
import {ConfigurationInterpreter} from '@viamedici-spc/configurator-ts';

import store from '../../../store';
import {conditional, conditionalWithConditions} from '../../../util/conditional';
import {getProductData} from '../../../util/getProductData';
import {filterAndJoin} from '../../../util/filterAndJoin';
import {getValueOfFeature, pimText, pimTextDebug} from '../../../util/values';
import {
	selectDimensionHeight,
	selectDimensionWidth,
	selectDimensionDepth,
	selectDimensionDiameter,
	selectWeight,
	selectWindsurfacearea,
	selectSpcConfiguration,
	selectSpcAttributes,
	selectProductData,
	selectSystemLumen,
	selectDimmLevel,
	selectCoefficients,
	selectHasCoefficients, selectSpcConfigurations
} from '../../../selectors';
import {regionCheck} from '../../../util/regionCheck';
import {calculateCurrent, calculatePower} from '../../../util/dimLevel';
import {attributeIdToString, stringToAttributeId} from '../../../components/spc/util/spcUtils';
import {iomoHeadTypeOptions} from '../../iomoModel';

@customElement('iomo-tender-text-head-data')
export class IomoTenderTaxeHeadData extends connect(store)(LitElement) {

	@property({type: Object})
	headData = null;

	static styles = [
		css`
			:host {
				margin: 0;
				display: flex;
				flex-direction: column;
				container-type: inline-size;
				padding-bottom: 2cqw;
			}

			.subheadline {
				font: var(--font-specsheet-print-small-text);
				font-size: 2cqw;
				margin-top: 5cqw;
				color: #939393;
			}
		`]
	;

	constructor() {
		super();
		this.productData = null;
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.debugShowAll = state.status.debugShowAll;
		this.lang = state.status.lang;
		this.lightProfile = state.lightProfile.data;
		this.conditionalVariantValues = state.productData.conditionalVariantValues;

		this.productData = selectProductData(this.state);
		this.spcConfiguration = selectSpcConfiguration(state);
		this.instanceId = this.headData.instanceId;

		// excluded attributes, that are defined individually in heads
		const excludeAttributes = [
			'LE_ReflectorColour_1',
			'LE_LightDistribution_1',
			'LE_ColourTemperature_1',
			'HS_HousingColour',
			'HS_HousingSize',
			'CX_Region',
			'HS_CircularBaseProfile',
			'LE_LuminousFlux_1',
			//'LE_DimmingLevel_1',
		];

		this.spcConfigurations = selectSpcConfigurations(state);
		if (this.spcConfigurations && Object.hasOwn(this.spcConfigurations, this.instanceId)) {
			this.spcConfiguration = this.spcConfigurations[this.instanceId];
		}

	}

	getValueFromProductData(featureId, featureValueId) {
		const pd = getProductData(featureId, this.productData);
		return pd?.values?.find(val => val.valueId === featureValueId);
	}

	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}

	render() {
		const height = Math.floor(this.headData.head.height  * 1000);
		const angle = this.headData.head.angle;
		const type = this.headData.head.type;
		const cap = this.headData.head.cap;
		const modifier = this.headData.head.modifier;
		const iomoHeadTypeOption = iomoHeadTypeOptions.find((option) => option.id === type);
		const typeDescription = iomoHeadTypeOption.description[this.lang];
		const typeLabel = iomoHeadTypeOption.label[this.lang];
		this.housingSize = this.headData.head.housingSize;

		const conditions = [
			'HS_HousingSize.' + this.headData.head.housingSize,
			'LE_ColourTemperature_1.' + this.headData.head.colourTemperature,
			'LE_LightDistribution_1.' + this.headData.head.lightDistribution,
			'LE_DimmingLevel_1.' + getValueOfFeature('root::LE_DimmingLevel_1', this.spcConfiguration),
			'LE_ReflectorColour_1.' + getValueOfFeature('root::LE_ReflectorColour_1', this.spcConfiguration),
		];

		const dimensionsLabel = this.t('TE_Dimensions', 'D');
		const dimensionWidth = conditionalWithConditions(['HS_HousingSize.' + this.housingSize], this.conditionalVariantValues, 'REDTAB.RETURN_Dimension_Width', 'EditorialTable', 1);
		const dimensionHeight = conditionalWithConditions(['HS_HousingSize.' + this.housingSize], this.conditionalVariantValues, 'REDTAB.RETURN_Dimension_Height', 'EditorialTable', 1);
		const dimensionDepth = conditionalWithConditions(['HS_HousingSize.' + this.housingSize], this.conditionalVariantValues, 'REDTAB.RETURN_Dimension_Depth', 'EditorialTable', 1);
		const dimensionDiameter = conditionalWithConditions(['HS_HousingSize.' + this.housingSize], this.conditionalVariantValues, 'Dimension_Diameter', 'EditorialTable', 1);
		const isDimensionsPresent = dimensionsLabel || dimensionDepth || dimensionWidth || dimensionHeight;
		const weight = conditionalWithConditions(['HS_HousingSize.' + this.housingSize], this.conditionalVariantValues, 'REDTAB.RETURN_LU_Weight', 'EditorialTable', 1);

		const wattageMax = conditionalWithConditions(conditions, this.conditionalVariantValues, 'REDTAB.RETURN_EB_Wattage_Max', 'EditorialTable', 1);
		const accessory = getValueOfFeature('root::LE_Accessories_1', this.spcConfiguration);

		const accessoryFactorValue = conditionalWithConditions(
			[
				'LE_Accessory.' + accessory,
				'LE_LightDistribution_1.' + this.headData.head.lightDistribution,
				'HS_HousingSize.' + this.headData.head.housingSize
			],
			this.conditionalVariantValues, 'REDTAB.RETURN_LE_AcessoryFactor', 'EditorialTable'
		);
		const accessoryFactor = accessoryFactorValue ? parseFloat(accessoryFactorValue.replace(',', '.')) : 1;
		// console.log('accessoryFactor', accessoryFactor, accessoryFactorValue);

		const applicableStandards = filterAndJoin([
			this.t(this.c('DescriptiveAttributeReference.LU_IK'), 'DV.S'),
			this.t(this.c('DescriptiveAttributeReference.LU_IP'), 'DV.S'),
			this.t(this.c('DescriptiveAttributeReference.LU_ENEC_CertificateDescription'), 'DV.S'),
			this.t(this.c('DescriptiveAttributeReference.LU_CE'), 'DV.S')
		], ', ');

		const descAtt = this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV.S');

		const maxLumenValue = getValueOfFeature('LE_DimmingLevel_1', this.spcConfiguration);
		const maxLumenPerWatt = Math.round(maxLumenValue / parseInt(wattageMax, 10) * 100) / 100;

		// get the sx number from CCV
		let sx = '';
		const conditionalVariantValues = this.state.productData.conditionalVariantValues;
		if (conditionalVariantValues) {
			sx = conditional(this.spcConfiguration, conditionalVariantValues, 'LU_LegacyArticleNumberGerman');
		}

		return html`
			${when(type === 'camera', () => html`
				${typeDescription}<br>
				${iomoHeadTypeOption.number}<br>
			`)}

			${when(type === 'speaker', () => html`
				${this.t('HS_HousingSize', 'FV.S', null, '', '')}
				${typeDescription}<br>
				${iomoHeadTypeOption.number[this.headData.head.housingSize]}<br>
			`)}

			${when(type === 'luminaire', () => html`
				${filterAndJoin([
					this.t('HS_HousingSize', 'FV.S', null, '', ''),
					this.t('LE_LightDistribution_1', 'FV.S', null, '', ''),
					this.t('LE_ColourTemperature_1', 'FV.S', null, '', ''),
					msg('max') + '. ' + this.t('LE_DimmingLevel_1', 'FV.S', null, '', ' lm'),
					this.t('LE_CRI', 'D', null, '', '') + this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV'),
					'ULOR 0%',
					this.t('EB_ElectricalProtectionClass_1', 'FV.S', null, '', ''),
				], ', ')}
				<br>

				${msg('max')}. ${this.t('LU_SystemWattage', 'D', null, '', '')} ${wattageMax} W,
				${maxLumenValue} lm / ${maxLumenPerWatt} lm/W
				${when(accessoryFactor !== 1, () => html`
					(${msg('incl.')} ${this.t('LE_Accessories_1', 'FV.S', null, '', ':')}
					${Math.round(maxLumenValue * accessoryFactor)} lm / ${Math.round(maxLumenPerWatt * accessoryFactor * 100) / 100} lm/W)
				`)}
				<br>

				<!-- Maße -->
				${this.t('TE_Dimensions', 'D', null, '', ':')}
				${dimensionDiameter ? html`${dimensionDiameter} mm,` : ''}
				${dimensionDepth ? html`${dimensionDepth} mm,` : ''}
				${dimensionWidth ? html`${dimensionWidth} mm,` : ''}
				${dimensionHeight ? html`${dimensionHeight} mm` : ''};

				<!-- Gewicht -->
				${this.t('LU_LuminaireWeight', 'D', null, '', ':')} ${weight} kg
				<br>

				<!-- Zubehör -->
				${accessory !== 'None' ?
					this.t('LE_Accessories_1', 'FV.S', null, '', ' ') +
					this.t('LE_Accessories_1', 'FV.L', null, '', '') + '<br>'
					: ''}

				<!-- Material -->
				<!--
				${filterAndJoin([
					this.t(this.c('DescriptiveAttributeReference.LE_LightEngineCover'), 'D.L', null, '', '')
				], ', ')}
				-->

				${this.t('LU_TechnicalRegion', 'D')}
				${this.t('LU_TechnicalRegion.220-240V 50/60 Hz', 'DV', null, '', ', ')}
				${this.t('LU_SurgeProtection', 'D')}
				${this.t('LU_SurgeProtection.10kV', 'DV.S', null, '', ', ')}
				<!-- ${this.t(this.c('DescriptiveAttributeReference.LU_AmbientTemperature'), 'DV.S', null, '', ', ')}-->
				${this.t('EB_EBlockOperatingGearDescription.ElectronicConverter', 'DV.S')}
				<br>

				${applicableStandards}<br>

				${sx}

			`)}
		`;
	}
}
